/**
 * F60b — "an admin action just changed something" signal.
 *
 * `adminFetch` emits after every successful non-GET request under `/admin/`
 * (sign-in, refresh and logout under `/admin/auth/` excluded). The
 * `NavCountsProvider` listens and refreshes the sidebar badges, so approving,
 * rejecting or settling anything updates its badge without per-page wiring.
 */

type Listener = (path: string) => void;

const listeners = new Set<Listener>();

/** Subscribe; returns the unsubscribe function. */
export function onAdminMutation(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitAdminMutation(path: string): void {
  for (const listener of listeners) {
    try {
      listener(path);
    } catch {
      // A listener must never break the request that triggered it.
    }
  }
}

const READ_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);

/** True for a state-changing admin API request (the caller checks the 2xx). */
export function isAdminMutation(method: string | undefined, path: string): boolean {
  if (READ_METHODS.has((method ?? 'GET').toUpperCase())) return false;
  const pathname = /^https?:\/\//.test(path) ? new URL(path).pathname : path;
  return pathname.startsWith('/admin/') && !pathname.startsWith('/admin/auth/');
}
